import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Session-scoped: entity_id lives only for the tab's session. This avoids
// leaking stale UI-state across users on shared machines. JWTs live in
// httpOnly cookies, so this key holds only a scope preference (not credentials).
const STORAGE_KEY = "ledger.entity_id";
// iter 72 — remember the user's Personal-vs-Business choice for the tab.
// Session-scoped so signing out on a shared device forces a fresh pick.
const WORKSPACE_KEY = "ledger.workspace_kind";
const CONSOLIDATED = "all";

function readEntityId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch (err) {
    // Storage disabled (private mode, cookies blocked, etc.) — safe to ignore
    // but surface for debugging.
    console.debug("[EntityContext] sessionStorage read failed:", err?.message);
    return null;
  }
}

function writeEntityId(id) {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.debug("[EntityContext] sessionStorage write failed:", err?.message);
  }
}

function readWorkspaceKind() {
  try { return sessionStorage.getItem(WORKSPACE_KEY); }
  catch { return null; }
}

const EntityContext = createContext({
  entities: [],
  currentEntityId: null,
  currentEntity: null,
  isConsolidated: false,
  loading: true,
  workspaceKind: null,
  refresh: async () => {},
  setEntity: () => {},
  clearWorkspaceKind: () => {},
});

export function EntityProvider({ children }) {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [currentEntityId, setCurrentEntityId] = useState(readEntityId);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/entities");
      setEntities(data);
      const existing = readEntityId();
      const isValid = existing === CONSOLIDATED || data.some((e) => e.id === existing);
      if (!isValid) {
        const def = data.find((e) => e.is_default) || data[0];
        if (def) {
          setCurrentEntityId(def.id);
          writeEntityId(def.id);
        }
      } else if (!currentEntityId) {
        setCurrentEntityId(existing);
      }
    } catch (err) {
      // Surface API errors instead of silently swallowing.
      console.warn("[EntityContext] Failed to load entities:", err?.response?.status, err?.message);
    } finally {
      setLoading(false);
    }
  }, [user, currentEntityId]);

  useEffect(() => {
    if (user) {
      refresh();
    } else {
      setEntities([]);
      setCurrentEntityId(null);
    }
    // refresh depends on currentEntityId to avoid loops; we intentionally key
    // on user identity here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setEntity = useCallback((id) => {
    const prev = currentEntityId;
    setCurrentEntityId(id);
    writeEntityId(id);
    window.dispatchEvent(new CustomEvent("ledger:entity-changed", { detail: { id } }));
    // Force a soft reload so every page re-fetches with the new X-Entity-Id header.
    if (prev && prev !== id) {
      setTimeout(() => window.location.reload(), 50);
    }
  }, [currentEntityId]);

  const value = useMemo(() => {
    const isConsolidated = currentEntityId === CONSOLIDATED;
    const currentEntity = isConsolidated
      ? { id: CONSOLIDATED, name: "Consolidated", icon: "🌐", color: "#2F4F4F", is_default: false, kind: "consolidated" }
      : entities.find((e) => e.id === currentEntityId) || null;
    const workspaceKind = readWorkspaceKind();
    const clearWorkspaceKind = () => {
      try { sessionStorage.removeItem(WORKSPACE_KEY); } catch { /* ignore */ }
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setCurrentEntityId(null);
    };
    return { entities, currentEntityId, currentEntity, isConsolidated, loading,
              workspaceKind, refresh, setEntity, clearWorkspaceKind };
  }, [entities, currentEntityId, loading, refresh, setEntity]);

  return <EntityContext.Provider value={value}>{children}</EntityContext.Provider>;
}

export function useEntity() {
  return useContext(EntityContext);
}

export const CONSOLIDATED_ID = CONSOLIDATED;
